import { stack, line, area, curveLinear } from "d3-shape"
import { scaleLinear } from "d3-scale"
import { axisRight } from "d3-axis"
import { createSelector } from "reselect"
import { chain, map } from "lodash"
import { findClosestDatum } from "../../helpers/find-closest-datum"
import { MARGINS, getHeight, getScaleX, getWidth } from "."
import { getBreakdownData } from "./data"
import { getBreakdownItems } from "./items"
import { roundPercentageFormatter } from "helpers/formatters/numbers"

const getBreakdownMinY = createSelector(
  [getBreakdownData, getBreakdownItems],
  (data, items) => data.reduce((min, datum) => {
    return Math.min(min, ...items.map(({ id }) => datum[id]).filter(datum => datum))
  }, Infinity),
)

const getBreakdownMaxY = createSelector(
  [getBreakdownData, getBreakdownItems],
  (data, items) => data.reduce((max, datum) => {
    return Math.max(max, ...items.map(({ id }) => datum[id]).filter(datum => datum))
  }, -Infinity),
)

export const getBreakdownScaleY = createSelector(
  [getBreakdownMinY, getBreakdownMaxY, getHeight],
  (minY, maxY, height) => {
    return scaleLinear()
      .domain([0, 1])
      .rangeRound([height - MARGINS.bottom, MARGINS.top])
  },
)

const getBreakdownStack = createSelector(
  [getBreakdownData, getBreakdownItems],
  (data, items) => stack().keys(map(items, "id"))(data),
)

export const getBreakdownAreas = createSelector(
  [getScaleX, getBreakdownScaleY, getBreakdownItems, getBreakdownStack],
  (scaleX, scaleY, items, stack) => {
    return chain(items)
      .map(({ id, area: areaColor, color }, i) => {
        const layer = stack[i]
        let lineShape = null

        if (i < (items.length - 1)) {
          lineShape = line()
            .x(d => scaleX(d.data.date))
            .y(d => scaleY(d[1]))
            .curve(curveLinear)
        }

        const areaShape = area()
          .x(d => scaleX(d.data.date))
          .y0(d => scaleY(d[0]))
          .y1(d => scaleY(d[1]))

        return ({
          id,
          line: lineShape && {
            d: lineShape(layer),
            style: { stroke: color, strokeWidth: 2, fill: "none" },
          },
          area: {
            d: areaShape(layer),
            style: { fill: areaColor },
          },
        })
      })
      .value()
  },
)

export const getBreakdownYAxis = createSelector(
  [getHeight, getWidth, getBreakdownScaleY],
  (height, width, scaleY) => ({
    fn: axisRight()
      .scale(scaleY)
      .ticks(4)
      .tickFormat(roundPercentageFormatter),
    transform: `translate(${width - (MARGINS.right + 7)},0)`,
    labelTransform: {
      x: width - 15,
      y: height / 2,
    },
    baseline: {
      x1: MARGINS.left,
      x2: MARGINS.left,
      y1: MARGINS.top,
      y2: height - MARGINS.bottom,
    },
  }),
)

export const getFindBreakdownByOffset = createSelector(
  [getBreakdownStack, getScaleX, getBreakdownItems, getBreakdownScaleY],
  (stack, scaleX, items, scaleY) => offset => {
    const lookup = scaleX.invert(offset - 18) // -2 is to give a margin of selection on the first element, -16 for the margin
    return items
      .map((item, i) => {
        if (i < (items.length - 1)) {
          const data = stack[i]
          // Match the chart-wide closest-datum rule (findByOffset) so hover dots and their
          // date field land on the same point the tooltip shows. Stack entries key the date
          // under d.data.date, so resolve against that and carry the original stack point.
          const closest = findClosestDatum(data.map(d => ({ date: d.data.date, stackPoint: d })), lookup)
          const dotData = closest?.stackPoint
          if (dotData) {
            return ({
              id: item.id,
              color: item.color,
              y: scaleY(dotData[1]),
              x: scaleX(dotData.data.date),
              date: dotData.data.date,
            })
          }
        }

        return null
      })
      .filter(item => item)
  },
)
